import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { Button } from './ui/button';
import { BrandLogo, BrandFooter, brandColors } from './Brand';
import {
  FileText,
  LayoutDashboard,
  Search,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Workflow,
  Brain,
  BarChart3,
  Sparkles,
  Eye,
  Zap,
  BookOpen,
  HeartPulse,
  Target,
  Gauge,
  BarChartHorizontal,
  ShieldCheck,
  ShieldAlert,
  CreditCard,
  DollarSign,
  TrendingUp
} from 'lucide-react';

import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import CreditBalanceWidget from './CreditBalanceWidget';

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, moduleEnabled, featureEnabled, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState(0);

  const loadPendingApprovals = useCallback(async () => {
    try {
      const res = await api.getMyTasks(getAuthHeaders());
      setPendingApprovals(Array.isArray(res.data) ? res.data.length : 0);
    } catch {
      /* silent — badge is a non-critical enhancement */
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!user) return;
    loadPendingApprovals();
    const id = setInterval(loadPendingApprovals, 60000);
    return () => clearInterval(id);
  }, [user, location.pathname, loadPendingApprovals]);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
    { path: '/documents', icon: FileText, labelKey: 'nav.documents' },
    { path: '/search', icon: Search, labelKey: 'nav.search' },
    { path: '/workflows', icon: Workflow, labelKey: 'nav.workflows' },
    { path: '/ai-extensions', icon: Sparkles, labelKey: 'nav.aiExtensions' },
    { path: '/chatbot', icon: MessageSquare, labelKey: 'nav.chatbot' },
    { path: '/vision-intelligence', icon: Eye, labelKey: 'Vision AI' },
    { path: '/enterprise-intelligence', icon: Zap, labelKey: 'Enterprise AI' },
    { path: '/billing', icon: CreditCard, labelKey: 'Billing & Plan' },
  ];

  // Show Analytics, Admin and Settings for SuperAdmin, CompanyAdmin, and TenantAdmin
  if (user?.roles?.some(role => ['SuperAdmin', 'CompanyAdmin', 'TenantAdmin'].includes(role))) {
    navItems.push({ path: '/analytics', icon: BarChart3, labelKey: 'nav.analytics' });
    navItems.push({ path: '/terminology-admin', icon: BookOpen, labelKey: 'Terminology' });
    navItems.push({ path: '/system-health', icon: HeartPulse, labelKey: 'System Health' });
    navItems.push({ path: '/accuracy', icon: Target, labelKey: 'Accuracy' });
    navItems.push({ path: '/executive', icon: Gauge, labelKey: 'Command Center' });
    navItems.push({ path: '/benchmarking', icon: BarChartHorizontal, labelKey: 'Benchmarking' });
    navItems.push({ path: '/board', icon: ShieldCheck, labelKey: 'Board Dashboard' });
    navItems.push({ path: '/admin', icon: Brain, labelKey: 'nav.admin' });
    navItems.push({ path: '/settings', icon: Settings, labelKey: 'nav.settings' });
  }

  // Revenue & Revenue Admin are SuperAdmin-only (not visible to Company/Tenant Admins)
  if (user?.roles?.includes('SuperAdmin')) {
    navItems.push({ path: '/security-center', icon: ShieldAlert, labelKey: 'Security Center' });
    navItems.push({ path: '/revenue', icon: TrendingUp, labelKey: 'Revenue' });
    navItems.push({ path: '/revenue-admin', icon: DollarSign, labelKey: 'Rev. Admin' });
  }

  // Plan-based Feature Matrix gating: hide AI module pages the tenant's plan
  // has fully disabled. SuperAdmin/exempt tenants are never gated (fail-open).
  const isSuper = user?.roles?.includes('SuperAdmin');
  const navGate = {
    '/vision-intelligence': () => moduleEnabled('Vision AI'),
    '/enterprise-intelligence': () => moduleEnabled('Enterprise AI'),
    '/ai-extensions': () => moduleEnabled('Agentic AI') || moduleEnabled('Agentic Extensions'),
    '/chatbot': () => featureEnabled('ai_qa'),
  };
  const visibleNavItems = isSuper
    ? navItems
    : navItems.filter((item) => (navGate[item.path] ? navGate[item.path]() : true));

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } transition-all duration-300 flex flex-col overflow-hidden`}
        style={{ backgroundColor: brandColors.primary }}
      >
        {/* Logo Section */}
        <div className="h-20 flex items-center justify-center px-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1.5">
              <BrandLogo size="small" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-white text-lg">ReasonDMS</span>
              <span className="text-xs" style={{ color: brandColors.accent }}>
                Secure Documents. Intelligent Decisions
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {visibleNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={`flex items-center gap-3 px-6 py-3 transition-all duration-200 ${
                isActive(item.path)
                  ? 'bg-white/20 text-white border-l-4'
                  : 'text-slate-300 hover:bg-white/10 hover:text-white border-l-4 border-transparent'
              }`}
              style={{ borderColor: isActive(item.path) ? brandColors.accent : 'transparent' }}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{t(item.labelKey)}</span>
              {item.path === '/workflows' && pendingApprovals > 0 && (
                <span
                  data-testid="workflows-approval-badge"
                  title={`${pendingApprovals} awaiting your approval`}
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold"
                >
                  {pendingApprovals > 99 ? '99+' : pendingApprovals}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <div className="mb-3 px-2">
            <p className="text-sm font-medium text-white truncate">{user?.full_name || user?.email}</p>
            <p className="text-xs text-slate-400">{user?.roles?.join(', ')}</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('nav.logout')}
          </Button>
        </div>

        {/* Powered by */}
        <div className="px-4 pb-3 text-center">
          <p className="text-xs text-slate-500">
            Powered by <span className="text-slate-400 font-medium">REASON</span>
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            {/* Collapsed state logo */}
            {!sidebarOpen && (
              <div className="flex items-center gap-2">
                <BrandLogo size="small" />
                <span className="font-bold text-lg" style={{ color: brandColors.primary }}>ReasonDMS</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <CreditBalanceWidget />
            <NotificationBell />
            <LanguageSwitcher compact />
            <div className="text-sm text-slate-600">
              <span className="font-medium" style={{ color: brandColors.primary }}>Company:</span>{' '}
              {user?.company?.name || user?.company_code}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            {children}
          </div>
          
          {/* Footer with Made in India branding */}
          <BrandFooter variant="minimal" />
        </main>
      </div>
    </div>
  );
};

export default Layout;
